import { GrowthCodeSchema } from '@capsico/contracts';

/**
 * Where this visit came from (W18 attribution), kept on the phone until the customer signs in:
 * a friend's / ambassador's / creator's code (`?ref=`), a restaurant QR scan (`?scan=` from the
 * /q redirect), a campaign (`?campaign=` or `?utm_campaign=`) and an offer code to pre-fill at
 * checkout (`?offer=`). None of it is secret. Entries older than the attribution window are
 * dropped.
 */
export interface GrowthEntry {
  refCode: string | null;
  scanToken: string | null;
  campaign: string | null;
  offerCode: string | null;
  at: number;
}

const KEY = 'capsico:customer:growth-entry';
const MAX_AGE_MS = 7 * 86_400_000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CAMPAIGN = /^[a-z0-9_-]{2,40}$/;
/** Query keys this module reads and removes from the address bar once stored. */
export const ENTRY_PARAMS = ['ref', 'scan', 'campaign', 'utm_campaign', 'offer', 'src'] as const;

const EMPTY: GrowthEntry = {
  refCode: null,
  scanToken: null,
  campaign: null,
  offerCode: null,
  at: 0,
};

function read(): GrowthEntry {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const value = JSON.parse(raw) as Partial<GrowthEntry>;
    if (typeof value.at !== 'number' || Date.now() - value.at > MAX_AGE_MS) return EMPTY;
    return {
      refCode: typeof value.refCode === 'string' ? value.refCode : null,
      scanToken: typeof value.scanToken === 'string' ? value.scanToken : null,
      campaign: typeof value.campaign === 'string' ? value.campaign : null,
      offerCode: typeof value.offerCode === 'string' ? value.offerCode : null,
      at: value.at,
    };
  } catch {
    return EMPTY;
  }
}

function write(entry: GrowthEntry): void {
  try {
    if (!entry.refCode && !entry.scanToken && !entry.campaign && !entry.offerCode) {
      window.localStorage.removeItem(KEY);
    } else {
      window.localStorage.setItem(KEY, JSON.stringify(entry));
    }
  } catch {
    // Storage blocked: attribution is best-effort.
  }
}

function cleanCode(value: string | null): string | null {
  if (!value) return null;
  const parsed = GrowthCodeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Reads the entry parameters from a query string and stores what is valid.
 * Returns true when the query carried any entry parameter (so the caller can tidy the URL).
 */
export function captureEntry(search: string): boolean {
  const params = new URLSearchParams(search);
  if (!ENTRY_PARAMS.some((key) => params.has(key))) return false;
  const current = read();
  const ref = cleanCode(params.get('ref'));
  const offer = cleanCode(params.get('offer'));
  const scanRaw = params.get('scan');
  const scan = scanRaw && UUID.test(scanRaw) ? scanRaw.toLowerCase() : null;
  const campaignRaw = (params.get('campaign') ?? params.get('utm_campaign') ?? '').toLowerCase();
  const campaign = CAMPAIGN.test(campaignRaw) ? campaignRaw : null;
  write({
    refCode: ref ?? current.refCode,
    scanToken: scan ?? current.scanToken,
    campaign: campaign ?? current.campaign,
    offerCode: offer ?? current.offerCode,
    at: ref || scan || campaign || offer ? Date.now() : current.at,
  });
  return true;
}

/** Query string without the entry parameters ('' when nothing is left). */
export function withoutEntryParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of ENTRY_PARAMS) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

export function pendingEntry(): GrowthEntry {
  return read();
}

export function clearTouch(): void {
  write({ ...read(), scanToken: null, campaign: null });
}

export function clearRefCode(): void {
  write({ ...read(), refCode: null });
}

/** Offer code to pre-fill at checkout (QR → WELCOME70, offer-launch links). */
export function pendingOfferCode(): string | null {
  return read().offerCode;
}

export function setPendingOfferCode(code: string | null): void {
  const current = read();
  write({ ...current, offerCode: cleanCode(code), at: current.at || Date.now() });
}

export function clearOfferCode(): void {
  write({ ...read(), offerCode: null });
}
